import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import React, { ReactNode } from "react";

import { Badge } from "../ui/badge";
import BlogAuthor from "../BlogAuthor";
import DateRender from "../DateRender";
import Link from "next/link";
import { WPRecentPost } from "@/types";
import { cn } from "@/lib/utils";
import { getBlogPostPreviewHref } from "@/lib/blog-post-preview-link";
import { useRouter } from "next/navigation";

interface PostPreviewTileProps {
  post: WPRecentPost;
  main?: boolean;
}

const ArticleLink = ({
  children,
  className,
  href,
}: {
  children: ReactNode;
  className?: string;
  href: string;
}) => {
  return (
    <Link className={className} href={href}>
      {children}
    </Link>
  );
};

const PostPreviewTile = ({ post, main }: PostPreviewTileProps) => {
  const { title, excerpt: preview, slug, id } = post.node;
  const headerImage = title.toLocaleLowerCase().includes("morsi")
    ? "https://www.sebastiangrantcdn.com/stone/wp-content/uploads/2025/10/Morsi.jpg"
    : post.image.sourceUrl;
  const authorName = post.node.author?.node?.name;
  const authorAvatar = post.node.author?.node?.avatar?.url;
  const postHref = getBlogPostPreviewHref({
    slug,
    title,
    image: headerImage,
    authorName,
    authorAvatar,
  });
  const router = useRouter();

  return (
    <Card
      className={cn("border-none bg-transparent shadow-none", !main && "h-full")}
      key={id}
    >
      <CardHeader
        onClick={() => router.push(postHref)}
        className={cn(
          main ? "aspect-video" : "h-full min-h-80 md:min-h-96",
          "cursor-pointer group overflow-hidden p-4 md:p-6 relative rounded-lg",
        )}
      >
        <ArticleLink
          className="absolute inset-0 block h-full w-full"
          href={postHref}
        >
          <div
            style={{
              backgroundImage: `url(${headerImage})`,
            }}
            className="h-full w-full bg-cover bg-center [transform:scale(1)] will-change-[transform,filter] blur-[2px] transition-[transform,filter] duration-700 ease-out group-hover:[transform:scale(1.05)] group-hover:blur-none"
          />
        </ArticleLink>

        {/* scaled slightly past edges so rounding/subpixel gaps can't show through */}
        <div className="absolute inset-0 scale-[1.1] bg-linear-to-t from-black/90 via-black/50 to-black/10 dark:from-black/80 dark:via-black/30 dark:to-transparent pointer-events-none" />
        <div className="relative z-10 mt-auto flex flex-col gap-2">
          <ArticleLink href={postHref}>
            <CardTitle
              className={cn(
                "text-white drop-shadow-sm",
                !main &&
                  "text-2xl leading-tight md:text-[1.7rem] lg:text-[2rem]",
              )}
            >
              {title}
            </CardTitle>
          </ArticleLink>
          <div className="pointer-events-auto">
            <BlogAuthor post={post} recentPostMode />
          </div>
        </div>

        <div className="absolute top-3 right-3 z-20">
          <Badge
            variant={"outline"}
            className="bg-background/80 backdrop-blur-sm border-neutral-300 text-xs font-Cabin"
          >
            <DateRender post={post} />
          </Badge>
        </div>
      </CardHeader>

      {main && (
        <Link href={postHref}>
          <CardContent className="px-0 pt-4 pb-0">
            <div
              className="relative max-h-24 md:max-h-none overflow-hidden mask-[linear-gradient(to_bottom,black_40%,transparent_100%)] md:mask-none font-Lora text-base leading-relaxed text-stone-700 dark:text-stone-300"
              dangerouslySetInnerHTML={{ __html: preview }}
            />
          </CardContent>
        </Link>
      )}
    </Card>
  );
};

export default PostPreviewTile;
